import * as React from 'react';
import {animate, view, createViewConfig, media} from '@sail/engine';
import {tokens as $} from '@sail/tokens';

import {useRef} from 'react';
import type {View} from '../view';

import {FocusScope} from './layers/FocusScope';
import {Button, ButtonConfig} from './Button';
import {useMediaQuery} from '../providers/UiMediaQueryProvider';
import {useResizeObserver} from './hooks/useResizeObserver';

export type CloseConfirmationDialogProps = View.IntrinsicElement<
  'div',
  {
    /**
     * All the strings we need
     */
    messages: {
      title: string;
      description: string;
      cancelAction: string;
      exitAction: string;
    };
    /**
     * The function to be called when the user confirms exiting
     */
    onClose: () => void;
    /**
     * The function to be called when the user cancels exiting
     */
    onCancel: () => void;

    onResize?: (entry: ResizeObserverEntry) => void;

    subviews?: View.Subviews<{
      wash: 'div';
      contentContainer: 'div';
      heading: 'div';
      content: 'div';
      footer: 'div';
    }>;
  }
>;

export const CloseConfirmationDialogConfig = createViewConfig({
  props: {} as CloseConfirmationDialogProps,
  name: 'CloseConfirmationDialog',
});

export const CloseConfirmationDialog = CloseConfirmationDialogConfig.createView(
  ({messages, onCancel, onClose, onResize, subviews, ...props}) => {
    const {isSmallScreen} = useMediaQuery();

    const containerRef = useRef<HTMLDivElement>(null);

    useResizeObserver({
      node: containerRef,
      handler: (entry) => onResize?.(entry),
    });

    return (
      <view.div
        css={{
          position: 'absolute',
          top: 'space.0',
          left: 'space.0',
          right: 'space.0',
          bottom: 'space.0',
        }}
        uses={[
          animate.inout({
            duration: 150,
            opacity: 0,
            easing: 'ease-in-out',
          }),
        ]}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.stopPropagation();
            onCancel();
          }
        }}
      >
        <view.div inherits={subviews.wash} />

        <view.div
          css={{
            stack: 'x',
            alignX: 'center',
            alignY: 'center',
            position: 'absolute',
            top: 'space.0',
            left: 'space.0',
            right: 'space.0',
            bottom: 'space.0',
          }}
        >
          <view.div {...props} ref={containerRef} role="dialog">
            <FocusScope contain autoFocus restoreFocus>
              <view.div inherits={subviews.contentContainer}>
                <view.h2 inherits={subviews.heading}>{messages.title}</view.h2>

                <view.section inherits={subviews.content}>
                  {messages.description}
                </view.section>

                <view.div
                  uses={[
                    isSmallScreen
                      ? ButtonConfig.customize({
                          defaults: {size: 'large'},
                          css: {width: 'fill'},
                        })
                      : null,
                  ]}
                  inherits={subviews.footer}
                >
                  <Button type="secondary" onPress={onCancel}>
                    {messages.cancelAction}
                  </Button>
                  <Button type="destructive" onPress={onClose}>
                    {messages.exitAction}
                  </Button>
                </view.div>
              </view.div>
            </FocusScope>
          </view.div>
        </view.div>
      </view.div>
    );
  },
  {
    css: {
      stack: 'y',
      width: 'fill',
      minHeight: 'fill',
      alignY: 'center',
      alignX: 'center',
      maxWidth: 'none',
      boxSizing: 'border-box',
      boxShadow: 'none',
      borderRadius: 'large',
      backdropFilter: 'blur(12px)',
    },

    subviews: {
      wash: {
        css: {
          backgroundColor: 'surface',
          opacity: 0.75,
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
        },
      },
      contentContainer: {
        css: {
          stack: 'y',
          alignY: 'center',
          maxWidth: '400px',
          padding: 'space.500',
          [media.up($['viewport.mobile'])]: {
            gap: 'xsmall',
          },
        },
      },
      heading: {
        css: {
          [media.up($['viewport.mobile'])]: {
            font: 'heading.small',
            marginBottom: 0,
          },
          [media.down($['viewport.mobile'])]: {
            font: 'heading.medium',
            margin: 0,
          },
        },
      },
      content: {
        css: {
          [media.up($['viewport.mobile'])]: {
            font: 'body.small',
          },
          [media.down($['viewport.mobile'])]: {
            font: 'body.medium',
            marginTop: 'xsmall',
          },
        },
      },
      footer: {
        css: {
          [media.up($['viewport.mobile'])]: {
            stack: 'x',
            gap: 'small',
            marginTop: 'space.150',
          },
          [media.down($['viewport.mobile'])]: {
            stack: 'y',
            gap: 'medium',
            marginTop: 'medium',
          },
        },
      },
    },
  },
);
